import { useEffect } from 'react'
import md5 from 'md5'
import { Modal, Button, Input, PasswordInput, Flex, Text, Select, Box } from '@mantine/core';
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useUserStore } from '../../store/useUserStore';

const UserModal = ({ onClose, isOpen, item }) =>
{
  const { postNewUser, updateUser, loading } = useUserStore();

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: Yup.object(validationSchema(!!item)),
    onSubmit: async (data) =>
    {
      const payload = { ...data };
      if (payload.password) {
        payload.password = md5(payload.password);
      }
      delete payload.repeat_password;
      payload.status = payload.status === 'true' || payload.status === true;
      
      try {
        if (!item) {
          await postNewUser(payload);
        } else {
          payload._id = item._id;
          await updateUser(payload);
        }
        onClose()
      } catch (error) {
        console.log(error)
      }
    }
  })

  const close = () =>
  {
    formik.resetForm()
    onClose()
  }

  useEffect(() =>
  {
    if (item) {
      formik.setFieldValue('username', item.username);
      formik.setFieldValue('name', item.name);
      formik.setFieldValue('email', item.email);
      formik.setFieldValue('role', item.role);
      formik.setFieldValue('status', item.status ? 'true' : 'false');
      formik.setFieldValue('password', '');
      formik.setFieldValue('repeat_password', '');
    } else {
      formik.resetForm();
    }
  }, [ item, isOpen ])

  return (
    <>
      <Modal 
        size="lg" 
        opened={ isOpen } 
        onClose={ close } 
        centered 
        radius="lg"
        title={<Text fw={700} size="lg">{ !item?._id ? 'Registrar Nuevo Usuario' : 'Editar Perfil de Usuario' }</Text>}
      >
        <Box className="flex flex-col gap-md p-xs">
          <Input.Wrapper label="Nombre Completo">
            <Input
              variant="filled"
              size="md"
              radius="md"
              placeholder="Ej: Juan Pérez"
              value={ formik.values.name }
              onChange={ formik.handleChange("name") }
            />
            { formik.touched.name && formik.errors.name && <Text c="red" fz="xs">{ formik.errors.name }</Text> }
          </Input.Wrapper>

          <Input.Wrapper label="Correo Electrónico">
            <Input
              variant="filled"
              size="md"
              radius="md"
              placeholder="email@ejemplo.com"
              value={ formik.values.email }
              onChange={ formik.handleChange("email") }
            />
            { formik.touched.email && formik.errors.email && <Text c="red" fz="xs">{ formik.errors.email }</Text> }
          </Input.Wrapper>

          <Input.Wrapper label="Nombre de Usuario">
            <Input
              variant="filled"
              size="md"
              radius="md"
              placeholder="username"
              value={ formik.values.username }
              onChange={ formik.handleChange("username") }
            />
            { formik.touched.username && formik.errors.username && <Text c="red" fz="xs">{ formik.errors.username }</Text> }
          </Input.Wrapper>

          <Box className="grid grid-cols-2 gap-md">
            <Input.Wrapper label="Rol de Acceso">
              <Select 
                placeholder='Selecciona un rol'
                variant="filled"
                size="md"
                radius="md"
                value={ formik.values.role }
                data={ [ "SUPER_ADMIN", "ADMIN", "USER" ] }
                onChange={ (value) => formik.setFieldValue("role", value) }
              />
              { formik.touched.role && formik.errors.role && <Text c='red' fz="xs">{ formik.errors.role }</Text> }
            </Input.Wrapper>

            <Input.Wrapper label="Estado de Cuenta">
              <Select 
                placeholder='Estado'
                variant="filled"
                size="md"
                radius="md"
                value={ formik.values.status }
                data={ [ 
                  { value: "true", label: "Activo" }, 
                  { value: "false", label: "Inactivo" } 
                ] }
                onChange={ (value) => formik.setFieldValue("status", value) }
              />
              { formik.touched.status && formik.errors.status && <Text c='red' fz="xs">{ formik.errors.status }</Text> }
            </Input.Wrapper>
          </Box>

          <Box className="grid grid-cols-2 gap-md">
            <Input.Wrapper label={item ? "Nueva Contraseña (Opcional)" : "Contraseña"}>
              <PasswordInput
                variant="filled"
                size="md"
                radius="md"
                placeholder="********"
                value={ formik.values.password }
                onChange={ formik.handleChange("password") }
              />
              { formik.touched.password && formik.errors.password && <Text c='red' fz="xs">{ formik.errors.password }</Text> }
            </Input.Wrapper>

            <Input.Wrapper label="Confirmar Contraseña">
              <PasswordInput
                variant="filled"
                size="md"
                radius="md"
                placeholder="********"
                value={ formik.values.repeat_password }
                onChange={ formik.handleChange("repeat_password") }
              />
              { formik.touched.repeat_password && formik.errors.repeat_password && <Text c='red' fz="xs">{ formik.errors.repeat_password }</Text> }
            </Input.Wrapper>
          </Box>
        </Box>

        <Flex justify="flex-end" gap="md" mt="xl" p="xs">
          <Button variant="subtle" color='gray' onClick={ close }>
            Cancelar
          </Button>
          <Button 
            loading={loading}
            onClick={ () => formik.handleSubmit() }
          > 
            { !item?._id ? 'Registrar Usuario' : 'Actualizar Perfil' }
          </Button>
        </Flex>
      </Modal>
    </>
  )
}

export default UserModal;

function initialValues ()
{
  return {
    name: '',
    email: '',
    username: '',
    password: '',
    repeat_password: '',
    status: 'true',
    role: ''
  }
}

function validationSchema (isEdit)
{
  return {
    name: Yup.string().required('El nombre es requerido'),
    email: Yup.string().email('Email inválido').required('El correo es requerido'),
    username: Yup.string().required('El usuario es requerido'),
    role: Yup.string().required('El rol es requerido'),
    status: Yup.string().required('El estado es requerido'),
    password: isEdit 
      ? Yup.string().min(6, 'Mínimo 6 caracteres')
      : Yup.string().min(6, 'Mínimo 6 caracteres').required('La contraseña es requerida'),
    repeat_password: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden')
      .when('password', {
        is: (val) => val && val.length > 0,
        then: (schema) => schema.required('Debes confirmar la contraseña')
      }),
  }
}
